import { useRef, useEffect, useCallback } from 'react';

import { TranscriptBlock as TranscriptBlockComponent } from './TranscriptBlock';
import { useDiarizationCorrection } from '../hooks/useDiarizationCorrection';
import { LiveTranscriptBlock } from '../types/transcriptTypes';
import { TranscriptSpeaker } from '@/api/types/backend-dto-types';

interface TranscriptBlocksProps {
  displayBlocks: LiveTranscriptBlock[];
  caseId: string;
  transcriptId: string;
  speakers: TranscriptSpeaker[];
  currentTime: number;
  onBlockClick?: (block: LiveTranscriptBlock) => void;
  onAcceptCorrection?: (correctionId: string, blockId: string) => void;
  onRejectCorrection?: (correctionId: string, blockId: string) => void;
  onUpdateBlock?: (blockId: string, content?: string, speakerId?: string | null) => void;
}

const getCurrentBlock = (blocks: LiveTranscriptBlock[], currentTime: number): LiveTranscriptBlock | null => {
  if (!blocks || blocks.length === 0) return null;

  const sortedBlocks = [...blocks].sort((a, b) => Number(a.timestamp) - Number(b.timestamp));

  // Find the block that contains the current time
  for (const block of sortedBlocks) {
    const blockStart = Number(block.timestamp);
    const blockDuration = Number(block.duration);

    if (currentTime >= blockStart) {
      if (blockDuration && !isNaN(blockDuration) && blockDuration > 0) {
        const blockEnd = blockStart + blockDuration;
        if (currentTime < blockEnd) {
          return block;
        }
      }
    }
  }

  return null;
};

export const TranscriptBlocks = ({
  displayBlocks,
  caseId,
  transcriptId,
  speakers,
  currentTime,
  onBlockClick,
  onAcceptCorrection,
  onRejectCorrection,
  onUpdateBlock,
}: TranscriptBlocksProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { handleAcceptCorrection, handleRejectCorrection } = useDiarizationCorrection({
    onAcceptCorrection,
    onRejectCorrection,
  });

  // Handle updates for both live (WebSocket) and saved (REST API) transcripts
  const handleUpdateBlock = useCallback(
    (blockId: string, content?: string, speakerId?: string | null) => {
      if (onUpdateBlock) {
        onUpdateBlock(blockId, content, speakerId);
      }
    },
    [onUpdateBlock],
  );

  const blocksToRender = displayBlocks || [];

  const currentBlock = getCurrentBlock(blocksToRender, currentTime);

  useEffect(() => {
    if (currentBlock && containerRef.current) {
      const blockElement = document.getElementById(`block-${currentBlock.id}`);
      if (blockElement) {
        blockElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentBlock?.id]);

  return (
    <div ref={containerRef} className="mx-auto max-w-4xl rounded bg-white p-6">
      <div className="space-y-1">
        {blocksToRender.map((block) => {
          const isActive = currentBlock?.id === block.id;

          return (
            <TranscriptBlockComponent
              key={block.id}
              block={block}
              caseId={caseId}
              transcriptId={transcriptId}
              speakers={speakers}
              isActive={isActive}
              onClick={onBlockClick ? () => onBlockClick(block) : undefined}
              onUpdate={handleUpdateBlock}
              onAcceptCorrection={handleAcceptCorrection}
              onRejectCorrection={handleRejectCorrection}
            />
          );
        })}
      </div>
    </div>
  );
};
